import catalog from "./catalog.json";

export const ACCESS_VERSION = 2;
export const ACCESS_CATALOG = catalog;
export const ACCESS_ROLES = {
  admin: "Administración",
  doctor: "Doctor",
  recepcionista: "Recepción",
  asistente: "Asistente",
  personalizado: "Personalizado",
};
export const CLINICAL_MODULES = [
  "imaging",
  "history",
  "treatment",
  "evolution",
  "photos",
  "odontogram",
  "periodonto",
  "consents",
  "prescriptions",
  "ceph",
  "bolton",
  "stl",
  "aligners",
];
const safeRole = (role) =>
  Object.hasOwn(ACCESS_ROLES, role) ? role : "personalizado";
const denied = () => ({
  version: ACCESS_VERSION,
  modules: Object.fromEntries(catalog.map((m) => [m.id, []])),
  patientScope: "assigned",
  agendaScope: "own",
});

export function roleAccess(role) {
  const result = denied();
  role = safeRole(role);
  const doctorModules = new Set([
    "dashboard",
    "appointments",
    "patients",
    ...CLINICAL_MODULES,
    "ai",
    "notifications",
  ]);
  const reception = {
    dashboard: ["read"],
    appointments: ["read", "create", "update", "cancel"],
    patients: ["read", "create", "update"],
    finance: ["read", "collect"],
    whatsapp: ["read", "send"],
    ai: ["read", "use"],
    notifications: ["read", "preferences"],
  };
  const assistant = {
    appointments: ["read"],
    patients: ["read"],
    history: ["read"],
    treatment: ["read"],
    evolution: ["read"],
    photos: ["read", "create"],
    odontogram: ["read"],
    periodonto: ["read"],
    notifications: ["read", "preferences"],
  };
  for (const module of catalog) {
    const all = Object.keys(module.actions);
    result.modules[module.id] =
      role === "admin"
        ? all
        : role === "doctor" && doctorModules.has(module.id)
          ? module.id === "imaging" ? ["read", "create", "annotate", "export"] : all.filter(
              (action) =>
                !["delete", "archive", "export", "cancel", "block"].includes(
                  action,
                ),
            )
          : role === "recepcionista"
            ? reception[module.id] || []
            : role === "asistente"
              ? assistant[module.id] || []
              : [];
  }
  if (["admin", "recepcionista"].includes(role)) {
    result.patientScope = "all";
    result.agendaScope = "team";
  }
  return result;
}

export function normalizeAccess(value, { strict = false } = {}) {
  if (!value || value.version !== ACCESS_VERSION) {
    if (strict) throw new Error("Revisa la versión de los permisos.");
    return denied();
  }
  if (
    !value.modules ||
    typeof value.modules !== "object" ||
    Array.isArray(value.modules)
  )
    throw new Error("El catálogo de permisos no es válido.");
  if (
    strict &&
    Object.keys(value.modules).some(
      (key) => !catalog.some((module) => module.id === key),
    )
  )
    throw new Error("Hay módulos desconocidos en los permisos.");
  if (
    !["all", "assigned"].includes(value.patientScope) ||
    !["team", "own"].includes(value.agendaScope)
  )
    throw new Error("Revisa los alcances de pacientes y agenda.");
  const result = {
    ...denied(),
    patientScope: value.patientScope,
    agendaScope: value.agendaScope,
  };
  for (const module of catalog) {
    const values = value.modules[module.id] || [];
    if (
      !Array.isArray(values) ||
      values.length > Object.keys(module.actions).length ||
      values.some(
        (action) =>
          typeof action !== "string" || !Object.hasOwn(module.actions, action),
      )
    )
      throw new Error("Revisa los permisos de " + module.label + ".");
    const actions = [...new Set(values)];
    if (actions.length && !actions.includes("read")) actions.unshift("read");
    result.modules[module.id] = actions;
  }
  if (result.modules.aligners.length && !result.modules.stl.includes("read"))
    result.modules.stl.unshift("read");
  // Opening clinical modules requires a minimal patient identity, never other clinical fields.
  if (
    [
      ...CLINICAL_MODULES,
      "appointments",
      "finance",
      "whatsapp",
      "dashboard",
      "campaigns",
    ].some((key) => result.modules[key].length) &&
    !result.modules.patients.includes("read")
  )
    result.modules.patients.unshift("read");
  return result;
}

export function effectiveAccess(member) {
  if (
    !member ||
    member.activo === false ||
    ["invitado", "desactivado", "revocado", "pending", "suspended"].includes(
      member.estado,
    )
  )
    return denied();
  if (
    member.isOwner === true ||
    member.puesto === "owner" ||
    member.rol === "owner" ||
    member.role === "owner"
  ) {
    return roleAccess("admin");
  }
  if (member.access != null) {
    try {
      return normalizeAccess(member.access);
    } catch {
      return denied();
    }
  }
  const legacy = member.permisos;
  if (legacy && typeof legacy === "object") {
    const result = denied();
    for (const module of catalog)
      if (module.legacy && legacy[module.legacy] === true)
        result.modules[module.id] = Object.keys(module.actions);
    if (legacy.pacientes === true && legacy.prescripciones == null)
      result.modules.prescriptions = Object.keys(
        catalog.find((m) => m.id === "prescriptions").actions,
      );
    if (Object.values(legacy).some((value) => value === true))
      result.modules.notifications = ["read", "preferences"];
    result.patientScope = "all";
    result.agendaScope = "team";
    return result;
  }
  return roleAccess(
    member.rol === "owner" || member.role === "owner" ? "admin" : member.rol,
  );
}

export function canAccess(member, module, action = "read") {
  return effectiveAccess(member).modules[module]?.includes(action) === true;
}

export function accessToLegacy(access) {
  const checked = normalizeAccess(access),
    has = (key) => checked.modules[key]?.includes("read");
  return {
    imagenologia: has("imaging"),
    pacientes: has("patients"),
    citas: has("appointments"),
    prescripciones: has("prescriptions"),
    finanzas: has("finance"),
    whatsapp: has("whatsapp"),
    usuarios: has("users"),
    configuracion: has("settings") || has("campaigns"),
    logs: has("activity"),
    dashboard: has("dashboard"),
    ai: has("ai"),
  };
}

export function isClinicalProvider(member) {
  if (
    !member ||
    member.activo === false ||
    ["invitado", "desactivado", "revocado", "pending", "suspended"].includes(
      member.estado,
    )
  )
    return false;
  return typeof member.atiendePacientes === "boolean"
    ? member.atiendePacientes
    : member.rol === "doctor";
}

export function isClinicOwner(member, clinic, uid) {
  return Boolean(
    (uid && [clinic?.ownerId, clinic?.ownerUid].includes(uid)) ||
    member?.isOwner === true ||
    member?.puesto === "owner" ||
    member?.rol === "owner" ||
    member?.role === "owner",
  );
}

export function accessIsSubset(next, authority) {
  const a = normalizeAccess(next),
    b = normalizeAccess(authority);
  return (
    !(a.patientScope === "all" && b.patientScope !== "all") &&
    !(a.agendaScope === "team" && b.agendaScope !== "team") &&
    catalog.every((module) =>
      a.modules[module.id].every((action) =>
        b.modules[module.id].includes(action),
      ),
    )
  );
}

export function setModuleAccess(access, id, actions) {
  return normalizeAccess({
    ...access,
    modules: { ...access.modules, [id]: actions },
  });
}
